import React, { cloneElement, isValidElement } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Form } from 'antd'
import classNames from 'classnames'

import { isNullable } from '@app/utils'
import { objectGet, objectHas } from '@app/utils/object'

import { VALIDATE_STATUS } from '../constants'

import styles from './styles.module.scss'

import type { ControllerRenderOptions } from '@app/types'
import type { ControllerWrapperProps, LntFormItemProps, ControllerComponentProps } from './types'

const ControllerWrapper: React.FC<ControllerWrapperProps> = ({ children: controller }) => <>{ controller }</>

export const LntFormItem: React.FC<LntFormItemProps> = ({
  name,
  help,
  defaultValue,
  validationOptions,
  removeRequiredMark,
  validateStatus: initialValidateStatus,
  className: outerClassName,
  children: child,
  ...props
}) => {
  const {
    control,
    formState: { errors }
  } = useFormContext()

  const className: string = classNames(outerClassName, styles.lntFormItem)

  if (isNullable(name) || !isValidElement(child)) {
    return (
      <Form.Item
        { ...props }
        className={ className }
      >
        { child }
      </Form.Item>
    )
  }

  const rules = objectGet(validationOptions, 'rules', undefined)
  const error = objectGet(errors, name, undefined)
  const hasRequiredRule = objectHas(rules, 'required') && !removeRequiredMark
  const helpMessage = (error?.message as React.ReactNode) ?? help
  const validateStatus = error?.message ? VALIDATE_STATUS.ERROR : initialValidateStatus

  return (
    <Form.Item
      { ...props }
      name={ name }
      className={ className }
      required={ hasRequiredRule }
      help={ helpMessage }
      validateStatus={ validateStatus }
    >
      <ControllerWrapper>
        <Controller
          { ...validationOptions }
          name={ name }
          defaultValue={ defaultValue }
          control={ control }
          render={ ({ field }: ControllerRenderOptions) =>
            cloneElement(child as React.ReactElement<ControllerComponentProps>, {
              ...field,
              id: name,
              checked: field.value
            })
          }
        />
      </ControllerWrapper>
    </Form.Item>
  )
}
